import React, { Fragment, useEffect } from 'react'
import { Form, Field } from 'react-final-form'
import { showAlertFailure } from 'client/methods'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import AuthFormFooter from 'components/auth/AuthFormFooter'
import AuthTitle from 'components/internal/AuthTitle'
import Button from 'components/buttons/Button'
import Card from 'components/cards/Card'
import Column from 'components/base/Column'
import FormattedPhoneInput from 'components/inputs/FormattedPhoneInput'
import Row from 'components/base/Row'
import useMutation from 'hooks/useMutation'
import validatePhone from 'lib/validatePhone'
import validateSchema from 'lib/validators'
import { SEND_MFA_CODE_MUTATION } from 'models/MFA'

const createSchema = t =>
  yup.object().shape({
    phoneNumber: yup
      .string()
      .required(t('mfa.form.fields.phoneNumber.validationMessage'))
      .test(
        'valid-phone',
        t('mfa.form.fields.phoneNumber.validationMessage'),
        validatePhone
      )
  })

function MfaBackupSmsPage() {
  const { t } = useTranslation()
  const history = useHistory()
  const { state } = useLocation() || {}

  const account = state?.account
  const session = state?.session
  const mfaContext = state?.mfaContext
  const isEnrollmentBackup = state?.isEnrollmentBackup

  useEffect(() => {
    if (!account || !session) {
      history.replace('/login')
    }
  }, [account, session, history])

  const [sendMfaCode, { loading }] = useMutation(SEND_MFA_CODE_MUTATION, {
    onCompleted: ({ sendMfaCode: { targetMasked } }) => {
      history.push({
        pathname: '/mfa/verify',
        state: {
          account,
          session,
          mfaContext: {
            ...(mfaContext || {}),
            backup_target_masked: targetMasked
          },
          channel: 'sms',
          isEnrollmentBackup
        }
      })
    },
    onError: () => showAlertFailure({ message: t('mfa.errors.sendCode') })
  })

  if (!account || !session) {
    return null
  }

  const schema = createSchema(t)

  const onFormSubmit = ({ phoneNumber }) =>
    sendMfaCode({
      channel: 'sms',
      target: phoneNumber,
      accessToken: session?.accessToken
    })

  const subtitle = isEnrollmentBackup
    ? t('mfa.backupSms.subtitleEnrollment')
    : t('mfa.backupSms.subtitle')

  return (
    <Fragment>
      <AuthTitle
        title={t('mfa.backupSms.title')}
        subtitle={subtitle}
        subtitlePosition="below"
      />

      <Row>
        <Column span={3} hiddenMd hiddenXs />
        <Column span={6} spanMd={12}>
          <Card variant="secondary">
            <Form
              onSubmit={onFormSubmit}
              validate={validateSchema(schema)}
              render={({ handleSubmit, submitting, invalid }) => (
                <form onSubmit={handleSubmit}>
                  <Field
                    component={FormattedPhoneInput}
                    name="phoneNumber"
                    type="tel"
                    placeholder={t('mfa.form.fields.phoneNumber.placeHolder')}
                    showValidationMessage
                    variant="medium"
                  />

                  <AuthFormFooter>
                    <Button
                      type="submit"
                      label={t('mfa.backupSms.confirmButton')}
                      disabled={submitting || invalid || loading}
                    />
                  </AuthFormFooter>
                </form>
              )}
            />
          </Card>
        </Column>
      </Row>
    </Fragment>
  )
}

export default MfaBackupSmsPage
